import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Globe2, Tag, BadgeCheck, Star, Users, Clock } from "lucide-react";
import api from "@/lib/api";
import { Tier, Empty } from "@/components/Shared";

const isRealCompany = (cid) => cid && cid !== "seed" && cid !== "catalog";

export default function ToolDetail() {
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [notfound, setNotfound] = useState(false);

  useEffect(() => {
    api.get(`/tools/${id}`).then((r) => setTool(r.data)).catch(() => setNotfound(true));
  }, [id]);

  if (notfound) return <Empty text="Tool not found." />;
  if (!tool) return <Empty text="Loading…" />;

  const coming = tool.availability === "coming_soon";

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="tool-detail-page">
      <Seo
        title={`${tool.name}${tool.category ? ` — ${tool.category}` : ""} | WorldAIInfo`}
        description={(tool.description || `Explore ${tool.name}, an AI tool${tool.company ? ` by ${tool.company}` : ""}. Features, pricing and availability on WorldAIInfo.`).slice(0, 300)}
        image={tool.logo_url}
        canonicalPath={`/tools/${id}`}
        type="article"
      />
      <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#00F0FF] mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to Marketplace
      </Link>
      <div className={`rounded-2xl p-8 ${tool.featured ? "card-surface featured-glow" : "card-surface"}`}>
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center font-display font-bold text-[#00F0FF] text-2xl overflow-hidden shrink-0">
            {tool.logo_url ? <img src={tool.logo_url} alt={tool.name} className="w-full h-full object-cover" /> : (tool.name[0] || "?")}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-extrabold text-3xl">{tool.name}</h1>
              <Tier tier={tool.tier} />
              {tool.badge && (
                <span className="font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/15 text-[#00F0FF]" data-testid="tool-detail-badge">{tool.badge}</span>
              )}
            </div>
            {tool.company && (
              isRealCompany(tool.company_id) ? (
                <Link to={`/company/${tool.company_id}`} data-testid="tool-detail-company"
                  className="text-sm text-[#94A3B8] hover:text-[#00F0FF] mt-1 inline-flex items-center gap-1">by {tool.company}
                  {tool.company_verified && <BadgeCheck className="w-4 h-4 text-[#00F0FF]" />}
                </Link>
              ) : (
                <p className="text-sm text-[#94A3B8] mt-1" data-testid="tool-detail-company">by {tool.company}</p>
              )
            )}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-[#94A3B8] font-mono-tech">
              {tool.rating ? <span className="flex items-center gap-1 text-[#F59E0B]"><Star className="w-3.5 h-3.5 fill-[#F59E0B]" /> {tool.rating}</span>
                : coming ? <span className="flex items-center gap-1 text-[#F59E0B]"><Clock className="w-3.5 h-3.5" /> Coming soon</span> : null}
              {tool.users && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {tool.users}</span>}
            </div>
          </div>
        </div>
        <p className="text-[#94A3B8] mt-6 leading-relaxed whitespace-pre-line">{tool.description}</p>

        {tool.tags && tool.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {tool.tags.map((tag) => (
              <span key={tag} className="font-mono-tech text-xs text-[#94A3B8] bg-white/5 border border-white/10 rounded px-2.5 py-1">{tag}</span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-4">
          <span className="font-mono-tech text-xs flex items-center gap-1.5 border border-white/10 rounded px-3 py-1.5 text-[#94A3B8]"><Tag className="w-3.5 h-3.5" /> {tool.category}</span>
          <span className="font-mono-tech text-xs flex items-center gap-1.5 border border-white/10 rounded px-3 py-1.5 text-[#94A3B8]"><Globe2 className="w-3.5 h-3.5" /> {tool.pricing_model}</span>
        </div>
        <a href={tool.website} target="_blank" rel="noreferrer" data-testid="tool-detail-visit"
          className="inline-flex items-center gap-2 mt-8 bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition">
          {coming ? "Learn more" : "Visit website"} <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
